import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Alert,
  Button,
  Link,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  CircularProgress,
  Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { useActiveOrgStore } from '@store/index';
import { billingService, type BillingStatus, type AvailablePlan } from '@services/billingService';
import { formatCurrency, formatDate } from '@utils/format';

/** A null/undefined/non-finite limit means "unlimited" (enterprise → Infinity → null over JSON). */
const isUnlimited = (limit: number | null | undefined) => limit === null || limit === undefined;

/** Format a Stripe-sourced price (cents) as a localized currency string. */
const formatAmount = (unitAmount: number, currency: string) =>
  formatCurrency(unitAmount / 100, (currency || 'usd').toUpperCase(), {
    maximumFractionDigits: unitAmount % 100 === 0 ? 0 : 2,
  });

/**
 * Billing / plan-management surface (REV-318). Shows the org's current plan,
 * subscription status and tracked-place usage, and drives self-serve Stripe
 * Checkout (upgrade) and the Billing Portal (manage). When billing is not yet
 * configured on the server it degrades to the informational plan list.
 */
function BillingPage() {
  const { t } = useTranslation();
  const { activeOrg } = useActiveOrgStore();
  const [searchParams] = useSearchParams();
  const checkoutResult = searchParams.get('checkout');

  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Which action is in flight: a plan type for checkout, or '__portal__'.
  const [pending, setPending] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!activeOrg) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      setStatus(await billingService.getStatus(activeOrg.id));
    } catch {
      setLoadError(t('billing.loadError'));
    } finally {
      setLoading(false);
    }
  }, [activeOrg, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCheckout = async (planType: string) => {
    if (!activeOrg) return;
    setPending(planType);
    setActionError(null);
    try {
      const url = await billingService.createCheckout(activeOrg.id, planType);
      window.location.assign(url);
    } catch {
      setActionError(t('billing.checkoutError'));
      setPending(null);
    }
  };

  const handleManage = async () => {
    if (!activeOrg) return;
    setPending('__portal__');
    setActionError(null);
    try {
      const url = await billingService.createPortal(activeOrg.id);
      window.location.assign(url);
    } catch {
      setActionError(t('billing.portalError'));
      setPending(null);
    }
  };

  const renderUsage = () => {
    if (!status) return null;
    const { used, limit } = status.usage;
    const text = isUnlimited(limit)
      ? t('billing.usageUnlimited', { used })
      : t('billing.usageValue', { used, limit });
    return (
      <Typography variant="body2" color="text.secondary">
        {t('billing.usageLabel')}: {text}
      </Typography>
    );
  };

  const subStatusLabel = (s: string | null) =>
    t(`billing.subStatus.${s ?? 'none'}`, { defaultValue: s ?? t('billing.subStatus.none') });

  const planCardAction = (plan: AvailablePlan) => {
    if (!status) return null;
    const isCurrent = plan.type === status.plan.type;
    if (isCurrent) {
      return <Chip size="small" color="primary" label={t('billing.currentPlanChip')} />;
    }
    // Invoice/PO orgs are managed by the account team — no self-serve switching.
    if (status.planSource === 'manual') return null;
    if (!plan.purchasable) {
      // Enterprise / non-self-serve tiers, or billing not configured server-side.
      if (!status.billingConfigured) return null;
      return (
        <Button size="small" component={RouterLink} to="/settings">
          {t('billing.contactSales')}
        </Button>
      );
    }
    if (!status.billingConfigured) return null;
    const busy = pending === plan.type;
    return (
      <Button
        size="small"
        variant="contained"
        onClick={() => handleCheckout(plan.type)}
        disabled={busy}
        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        {t('billing.switchTo', { plan: plan.displayName })}
      </Button>
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Link
        component={RouterLink}
        to="/settings"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 2 }}
      >
        <ArrowBackIcon fontSize="small" />
        {t('billing.backToSettings')}
      </Link>

      <Typography variant="h4" component="h1" fontWeight={700} sx={{ mb: 3 }}>
        {t('billing.title')}
      </Typography>

      {checkoutResult === 'success' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('billing.checkoutSuccess')}
        </Alert>
      )}
      {checkoutResult === 'cancelled' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('billing.checkoutCancelled')}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      )}

      {!activeOrg && <Alert severity="info">{t('billing.noOrg')}</Alert>}

      {activeOrg && loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress aria-label={t('billing.title')} />
        </Box>
      )}

      {activeOrg && !loading && loadError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={load}>
              {t('billing.retry')}
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}

      {activeOrg && !loading && status && (
        <>
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ sm: 'center' }}
              spacing={2}
            >
              <Box>
                <Typography variant="overline" color="text.secondary">
                  {t('billing.currentPlanTitle')}
                </Typography>
                <Typography variant="h5" component="h2" fontWeight={700}>
                  {status.plan.displayName}
                </Typography>
                <Box sx={{ mt: 0.5, mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={subStatusLabel(status.subscription.status)}
                  />
                  {status.plan.retired && (
                    <Chip size="small" color="warning" label={t('billing.legacyBadge')} />
                  )}
                </Box>
                {renderUsage()}
                {status.plan.retired && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t('billing.legacyNote')}
                  </Typography>
                )}
              </Box>
              {status.planSource !== 'manual' &&
                status.subscription.hasBillingAccount &&
                status.billingConfigured && (
                  <Button
                    variant="outlined"
                    onClick={handleManage}
                    disabled={pending === '__portal__'}
                    startIcon={
                      pending === '__portal__' ? <CircularProgress size={16} /> : undefined
                    }
                  >
                    {t('billing.manageBilling')}
                  </Button>
                )}
            </Stack>
          </Paper>

          {status.planSource === 'manual' && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t('billing.managedTitle')}
              </Typography>
              {t('billing.managedBody')}
              {status.subscription.currentPeriodEnd && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('billing.expiresOn', {
                    date: formatDate(status.subscription.currentPeriodEnd),
                  })}
                </Typography>
              )}
            </Alert>
          )}

          {status.planSource !== 'manual' && !status.billingConfigured && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t('billing.comingSoonTitle')}
              </Typography>
              {t('billing.comingSoonBody')}
            </Alert>
          )}

          <Typography variant="h5" component="h2" fontWeight={700} sx={{ mb: 1 }}>
            {t('billing.plansTitle')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {t('billing.plansIntro')}
          </Typography>

          <Grid container spacing={2}>
            {status.availablePlans.map((plan) => (
              <Grid item xs={12} sm={6} md={3} key={plan.type}>
                <Card
                  variant="outlined"
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="h3" gutterBottom>
                      {plan.displayName}
                    </Typography>
                    {plan.price && plan.price.unitAmount != null && (
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                        {t('billing.price', {
                          amount: formatAmount(plan.price.unitAmount, plan.price.currency),
                          interval: plan.price.interval
                            ? t(`billing.intervals.${plan.price.interval}`, {
                                defaultValue: plan.price.interval,
                              })
                            : '',
                        })}
                      </Typography>
                    )}
                    <Chip
                      size="small"
                      label={
                        isUnlimited(plan.contextLimit)
                          ? t('billing.plans.unlimitedContexts')
                          : t('billing.plans.contexts', { count: plan.contextLimit as number })
                      }
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                      <CheckIcon fontSize="small" color="success" />
                      <Typography variant="body2" color="text.secondary">
                        {t('billing.plans.freeScanning')}
                      </Typography>
                    </Box>
                  </CardContent>
                  <CardActions>{planCardAction(plan)}</CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Container>
  );
}

export default BillingPage;
